/**
 * 충돌 감지 및 해결 로직
 */

import type { CachedMachine, ConflictInfo } from "./types";

export enum ConflictResolution {
  KEEP_LOCAL = "keepLocal",
  KEEP_SERVER = "keepServer",
  MERGE = "merge",
}

interface ServerMachineData {
  _id: string;
  machineId: string;
  name: string;
  model: string;
  checkList: Array<{
    _id: string;
    category: string;
    item: string;
    result?: string;
    note?: string;
  }>;
  dataList: Array<{
    _id: string;
    category: string;
    item: string;
    value: string;
    unit?: string;
    note?: string;
  }>;
  pictures?: Array<{
    _id: string;
    url: string;
    caption?: string;
  }>;
  measures?: Record<string, unknown[]>;
  updatedAt: string;
}

/**
 * 로컬 데이터와 서버 데이터 간 충돌 감지
 */
export function detectConflict(local: CachedMachine, server: ServerMachineData): ConflictInfo {
  // 로컬 변경사항이 없으면 충돌 없음
  if (!local.localChanges || local.syncStatus === "synced") {
    return {
      hasConflict: false,
      localData: local,
      serverData: server,
      fields: [],
      localUpdatedAt: local.cachedAt,
      serverUpdatedAt: new Date(server.updatedAt).getTime(),
    };
  }

  const conflictFields: string[] = [];

  // 체크리스트 비교
  if (hasChecklistDifference(local.checkList, server.checkList)) {
    conflictFields.push("checkList");
  }

  // 데이터 리스트 비교
  if (hasDataListDifference(local.dataList, server.dataList)) {
    conflictFields.push("dataList");
  }

  // 계측 데이터 비교
  if (hasMeasuresDifference(local.measures, server.measures)) {
    conflictFields.push("measures");
  }

  return {
    hasConflict: conflictFields.length > 0,
    localData: local,
    serverData: server,
    fields: conflictFields,
    localUpdatedAt: local.cachedAt,
    serverUpdatedAt: new Date(server.updatedAt).getTime(),
  };
}

/**
 * 체크리스트 차이 감지
 */
function hasChecklistDifference(
  local: CachedMachine["checkList"],
  server: ServerMachineData["checkList"]
): boolean {
  if (local.length !== server.length) return true;

  for (const localItem of local) {
    const serverItem = server.find((s) => s._id === localItem._id);
    if (!serverItem) return true;

    if (localItem.result !== serverItem.result || localItem.note !== serverItem.note) {
      return true;
    }
  }

  return false;
}

/**
 * 데이터 리스트 차이 감지
 */
function hasDataListDifference(
  local: CachedMachine["dataList"],
  server: ServerMachineData["dataList"]
): boolean {
  if (local.length !== server.length) return true;

  for (const localItem of local) {
    const serverItem = server.find((s) => s._id === localItem._id);
    if (!serverItem) return true;

    if (localItem.value !== serverItem.value || localItem.note !== serverItem.note) {
      return true;
    }
  }

  return false;
}

/**
 * 계측 데이터 차이 감지
 * 각 계측 타입별 배열 길이 비교 + JSON 직렬화 비교
 */
function hasMeasuresDifference(
  local: CachedMachine["measures"],
  server: ServerMachineData["measures"]
): boolean {
  // 둘 다 없으면 동일
  if (!local && !server) return false;
  // 한쪽만 있으면 차이
  if (!local || !server) return true;

  const localKeys = Object.keys(local).filter(
    (k) =>
      Array.isArray((local as Record<string, unknown[]>)[k]) &&
      (local as Record<string, unknown[]>)[k].length > 0
  );
  const serverKeys = Object.keys(server).filter(
    (k) => Array.isArray(server[k]) && server[k].length > 0
  );

  // 유효한 타입 키 수가 다르면 차이
  if (localKeys.length !== serverKeys.length) return true;

  for (const key of localKeys) {
    const localArr = (local as Record<string, unknown[]>)[key];
    const serverArr = server[key];
    if (!serverArr) return true;
    if (localArr.length !== serverArr.length) return true;
    // 항목 수준 비교: JSON 직렬화로 간단히 비교
    if (JSON.stringify(localArr) !== JSON.stringify(serverArr)) return true;
  }

  return false;
}

/**
 * 충돌 해결
 */
export function resolveConflict(
  conflict: ConflictInfo,
  resolution: ConflictResolution
): Partial<CachedMachine> {
  const local = conflict.localData as CachedMachine;
  const server = conflict.serverData as CachedMachine; // 서버 데이터도 CachedMachine으로 변환되어 전달됨

  switch (resolution) {
    case ConflictResolution.KEEP_LOCAL:
      return {
        checkList: local.checkList,
        dataList: local.dataList,
        pictures: local.pictures,
        measures: local.measures,
      };

    case ConflictResolution.KEEP_SERVER:
      return {
        checkList: server.checkList,
        dataList: server.dataList,
        pictures: server.pictures,
        measures: server.measures,
      };

    case ConflictResolution.MERGE:
      // 기본 병합 전략: 더 최신 데이터 기준
      const useLocal = conflict.localUpdatedAt > conflict.serverUpdatedAt;
      return {
        checkList: useLocal ? local.checkList : server.checkList,
        dataList: useLocal ? local.dataList : server.dataList,
        pictures: useLocal ? local.pictures : server.pictures,
        measures: useLocal ? local.measures : server.measures,
      };

    default:
      return {
        checkList: server.checkList,
        dataList: server.dataList,
        pictures: server.pictures,
        measures: server.measures,
      };
  }
}

/**
 * 충돌 목록 생성 (사용자에게 표시용)
 */
export function formatConflictDetails(conflict: ConflictInfo): string[] {
  const details: string[] = [];

  if (conflict.fields.includes("checkList")) {
    details.push("점검항목이 서버와 다릅니다.");
  }

  if (conflict.fields.includes("dataList")) {
    details.push("측정 데이터가 서버와 다릅니다.");
  }

  if (conflict.fields.includes("measures")) {
    details.push("계측 데이터가 서버와 다릅니다.");
  }

  return details;
}
